import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import radixService, { radixQueryKeys } from "api/radixService";
import { ERadixProgram, IRadixSettings } from "dto/Radix/IRadix";
import { ENotificationType, notification } from "utils/notification";
import { radixErrorText } from "modules/radixModule/radixUtils";

import styles from "./styles.module.scss";

// Formularz podłączenia / edycji integracji RADIX (Ustawienia → Integracje → „Dodaj
// integrację" → RADIX, „Edytuj" na karcie RADIX oraz koło zębate w module RADIX).
// Port konfiguracji z Magistratu: łańcuchy połączeń do baz MS SQL programów RADIX
// (Kwantum.Radix.WIP/POGRUN/GOK Properties.Settings — tam w konfiguracji usługi, tu per
// firma) + miejscowości lokalne (Ustawienia urzędu → RadixPlaces: adresaci z tych
// miejscowości dostają doręczenie osobiste zamiast poczty). Zapisane łańcuchy NIE wracają
// z BE — pokazujemy „skonfigurowane"; wpisanie nowego podmienia, „usuń" kasuje.

type ProgramKey = "Wip" | "Pogrun" | "Gok";

const PROGRAMS: { program: ERadixProgram; label: string; key: ProgramKey }[] = [
  { program: ERadixProgram.WIP, label: "WIP — windykacja i płatności (upomnienia)", key: "Wip" },
  { program: ERadixProgram.PoGrun, label: "PoGrun — podatki od nieruchomości i gruntów (decyzje)", key: "Pogrun" },
  { program: ERadixProgram.GOK, label: "GOK — gospodarka odpadami (zawiadomienia)", key: "Gok" },
];

const has = (s: IRadixSettings | undefined, key: ProgramKey) =>
  key === "Wip" ? !!s?.HasWip : key === "Pogrun" ? !!s?.HasPogrun : !!s?.HasGok;

interface Props {
  toggle: () => void;
  onSuccess: () => void;
  /** Tryb edycji istniejącej integracji (inny tytuł, przycisk „Zapisz zmiany"). */
  isUpdate?: boolean;
  /** Bez własnego nagłówka/opisu (osadzenie w modalu modułu RADIX). */
  embedded?: boolean;
}

const ConfigureRadix = ({ toggle, onSuccess, isUpdate = false, embedded = false }: Props) => {
  const queryClient = useQueryClient();
  const { data: settingsData, isLoading } = radixService.useGetSettings();
  const { mutate: save, isLoading: saving } = radixService.useSaveSettings();
  const { mutate: test, isLoading: testing } = radixService.useTestConnection();
  const settings = settingsData?.data;

  const [conn, setConn] = useState<Record<ProgramKey, string>>({ Wip: "", Pogrun: "", Gok: "" });
  const [clear, setClear] = useState<Record<ProgramKey, boolean>>({ Wip: false, Pogrun: false, Gok: false });
  const [places, setPlaces] = useState<string[]>([]);
  const [newPlace, setNewPlace] = useState("");
  const [testResult, setTestResult] = useState<Partial<Record<ProgramKey, { ok: boolean; message: string }>>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) setPlaces(settings.Places);
  }, [settings]);

  const doTest = (p: (typeof PROGRAMS)[number]) => {
    const value = conn[p.key].trim();
    test(
      { Program: p.program, ConnectionString: value || null },
      {
        onSuccess: (res) => setTestResult((r) => ({ ...r, [p.key]: { ok: res.data.Ok, message: res.data.Message ?? "" } })),
        onError: (e) => setTestResult((r) => ({ ...r, [p.key]: { ok: false, message: radixErrorText(e, "Test nie powiódł się.") } })),
      },
    );
  };

  const addPlace = () => {
    const v = newPlace.trim().replace(/;/g, "");
    if (!v) return;
    if (!places.some((x) => x.toLowerCase() === v.toLowerCase())) setPlaces([...places, v]);
    setNewPlace("");
  };

  // Coś do zapisania: nowy łańcuch, usunięcie albo zmiana miejscowości.
  const anyConnectionAfterSave = PROGRAMS.some((p) => (clear[p.key] ? false : conn[p.key].trim() ? true : has(settings, p.key)));
  const placesChanged = JSON.stringify(places) !== JSON.stringify(settings?.Places ?? []);
  const dirty = PROGRAMS.some((p) => conn[p.key].trim().length > 0 || clear[p.key]) || placesChanged;
  const canSave = !!settings?.CanManage && !saving && dirty && (isUpdate || anyConnectionAfterSave);

  const submit = () => {
    setError(null);
    const pick = (key: ProgramKey) => (clear[key] ? "" : conn[key].trim() ? conn[key].trim() : null);
    save(
      { WipConnectionString: pick("Wip"), PogrunConnectionString: pick("Pogrun"), GokConnectionString: pick("Gok"), Places: places },
      {
        onSuccess: (res) => {
          queryClient.setQueryData(radixQueryKeys.settings, res);
          notification({ type: ENotificationType.SUCCESS, title: "RADIX", text: "Zapisano ustawienia integracji RADIX." });
          onSuccess();
        },
        onError: (e) => setError(radixErrorText(e, "Nie udało się zapisać ustawień.")),
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      {!embedded && (
        <>
          <div className={styles.title}>{isUpdate ? "Edycja integracji RADIX" : "RADIX (WIP / PoGrun / GOK)"}</div>
          <div className={styles.body}>
            Import paczek decyzji podatkowych, upomnień i zawiadomień z programów RADIX: wydruk RTF jest dzielony na
            pisma, a każde dostaje przesyłkę wychodzącą do biura podawczego. Nota łączy się bezpośrednio z bazami
            MS SQL programów — podaj łańcuchy połączeń (np.{" "}
            <span className={styles.mark}>Data Source=serwer;Initial Catalog=Radix_WIP;User ID=…;Password=…</span>).
          </div>
        </>
      )}

      <div className={styles.form}>
        {isLoading || !settings ? (
          <div className={styles.hint}>Ładowanie…</div>
        ) : (
          <>
            {!settings.CanManage && (
              <div className={styles.error}>Ustawienia RADIX może zmieniać właściciel firmy, administrator albo rola z uprawnieniem do integracji.</div>
            )}

            {PROGRAMS.map((p) => (
              <div key={p.key} className={styles.program}>
                <div className={styles.programHead}>
                  {p.label}
                  <span className={`${styles.state} ${has(settings, p.key) ? "" : styles.stateNone}`}>
                    {has(settings, p.key) ? "skonfigurowane" : "brak"}
                  </span>
                </div>
                <div className={styles.row}>
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder={has(settings, p.key) ? "(bez zmian — wpisz, aby podmienić)" : "łańcuch połączenia MS SQL"}
                    value={conn[p.key]}
                    disabled={clear[p.key] || !settings.CanManage}
                    onChange={(e) => setConn({ ...conn, [p.key]: e.target.value })}
                  />
                  <HollowButton
                    text="Testuj"
                    onClick={() => doTest(p)}
                    disabled={testing || !settings.CanManage || (!conn[p.key].trim() && !has(settings, p.key))}
                  />
                </div>
                {has(settings, p.key) && (
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      checked={clear[p.key]}
                      disabled={!settings.CanManage}
                      onChange={(e) => setClear({ ...clear, [p.key]: e.target.checked })}
                    />
                    usuń zapisane połączenie
                  </label>
                )}
                {testResult[p.key] && (
                  <div className={testResult[p.key]!.ok ? styles.ok : styles.error}>{testResult[p.key]!.message}</div>
                )}
              </div>
            ))}

            <div className={styles.program}>
              <div className={styles.programHead}>Miejscowości lokalne (doręczenie osobiste)</div>
              <div className={styles.row}>
                <input
                  type="text"
                  placeholder="Nazwa miejscowości"
                  value={newPlace}
                  disabled={!settings.CanManage}
                  onChange={(e) => setNewPlace(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addPlace();
                    }
                  }}
                />
                <HollowButton text="Dodaj" onClick={addPlace} disabled={!newPlace.trim() || !settings.CanManage} />
              </div>
              {places.length > 0 && (
                <div className={styles.places}>
                  {places.map((p) => (
                    <div key={p} className={styles.placeRow}>
                      <span>{p}</span>
                      {settings.CanManage && (
                        <button type="button" className={styles.linkBtn} onClick={() => setPlaces(places.filter((x) => x !== p))}>
                          usuń
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.hint}>
                Przesyłki do adresatów z tych miejscowości dostają przy imporcie formę „Doręczenie osobiste" zamiast wybranego rodzaju przesyłki.
              </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}
          </>
        )}

        <div className={styles.buttonsWrapper}>
          <HollowButton text="Anuluj" onClick={toggle} />
          <Button text={isUpdate ? "Zapisz zmiany" : "Zapisz"} onClick={submit} loading={saving} disabled={!canSave} />
        </div>
      </div>
    </div>
  );
};

export default ConfigureRadix;
